import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, mediaUrl, formatApiErrorDetail, CATEGORIES } from "../lib/api";
import { Loader2, Plus, Pencil, Trash2, LogOut, Package, Inbox, Layers, Upload, X, ArrowLeft, FileText, Check, Phone, RefreshCw } from "lucide-react";

const CATS = CATEGORIES.filter((c) => c !== "All Products");
const EMPTY = { name: "", category: CATS[0], brand: "", description: "", image_url: "", datasheet_url: "", featured: false, specsText: "" };

function Login() {
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-ink border border-white/10 px-4 py-3 text-white placeholder:text-slate-600 text-sm focus:border-signal focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid place-items-center tech-grid px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-card border border-white/10 p-8" data-testid="admin-login-form">
        <Link to="/" className="font-mono text-[11px] text-slate-500 hover:text-signal flex items-center gap-1 mb-6"><ArrowLeft className="w-3 h-3" /> Back to site</Link>
        <div className="font-head font-black uppercase text-3xl text-white mb-1">Admin Portal</div>
        <div className="font-mono text-[11px] text-signal tracking-widest mb-8">DASHMESH ENTERPRISES</div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 block">Email</label>
        <input className={field} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" placeholder="admin@dashmeshtech.com" />
        <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 block mt-4">Password</label>
        <input type="password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" placeholder="••••••••" />
        <button disabled={busy} data-testid="admin-login-btn"
          className="mt-6 w-full font-head font-bold uppercase tracking-wider px-6 py-3 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
        </button>

        <div className="flex items-center gap-3 my-6">
          <span className="flex-1 h-px bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">or</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <button type="button" onClick={() => loginWithGoogle("/admin")} data-testid="admin-google-signin"
          className="w-full font-mono text-xs uppercase tracking-wider px-6 py-3 border border-white/15 text-white hover:border-signal hover:text-signal transition-colors flex items-center justify-center gap-2">
          <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.65 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.5 14.7 2.5 12 2.5 6.9 2.5 2.8 6.6 2.8 12S6.9 21.5 12 21.5c5.9 0 9.8-4.1 9.8-9.9 0-.7-.1-1.2-.2-1.7H12z"/></svg>
          Continue with Google
        </button>
      </form>
    </div>
  );
}

function ProductForm({ initial, onDone, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, datasheet_url: data.url }));
      toast.success("Datasheet uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingDoc(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    let specs = {};
    (form.specsText || "").split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) specs[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    });
    const payload = {
      name: form.name, category: form.category, brand: form.brand, description: form.description,
      image_url: form.image_url, datasheet_url: form.datasheet_url, featured: !!form.featured, specs,
    };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success(form.id ? "Product updated" : "Product created");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-ink border border-white/10 px-4 py-2.5 text-white placeholder:text-slate-600 text-sm focus:border-signal focus:outline-none transition-colors";
  const label = "font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5 block";

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/85 backdrop-blur-sm p-4 overflow-y-auto" data-testid="product-form-modal">
      <form onSubmit={submit} className="w-full max-w-2xl bg-card border border-white/10 p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-head font-black uppercase text-2xl text-white">{form.id ? "Edit Product" : "New Product"}</h3>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-signal"><X /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={label}>Name *</label>
            <input className={field} value={form.name} onChange={set("name")} data-testid="pf-name" required /></div>
          <div><label className={label}>Category *</label>
            <select className={field} value={form.category} onChange={set("category")} data-testid="pf-category">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className={label}>Brand *</label>
            <input className={field} value={form.brand} onChange={set("brand")} data-testid="pf-brand" required /></div>
          <div className="sm:col-span-2"><label className={label}>Description *</label>
            <textarea rows={3} className={field} value={form.description} onChange={set("description")} data-testid="pf-description" required /></div>
          <div className="sm:col-span-2"><label className={label}>Specs (one per line — "Key: Value")</label>
            <textarea rows={3} className={field} value={form.specsText} onChange={set("specsText")} data-testid="pf-specs" placeholder="Torque: 45 Nm&#10;Ratio: 100:1" /></div>
          <div className="sm:col-span-2"><label className={label}>Product Image</label>
            <div className="flex items-center gap-3">
              {form.image_url && <img src={mediaUrl(form.image_url)} alt="" className="w-16 h-16 object-cover border border-white/10" />}
              <label className="cursor-pointer font-mono text-xs uppercase tracking-wider px-4 py-2.5 border border-white/10 text-slate-300 hover:border-signal hover:text-signal transition-colors flex items-center gap-2">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="pf-image-upload" />
              </label>
              <input className={field + " flex-1"} value={form.image_url} onChange={set("image_url")} data-testid="pf-image-url" placeholder="or paste image URL" />
            </div></div>
          <div className="sm:col-span-2"><label className={label}>Datasheet (PDF)</label>
            <div className="flex items-center gap-3">
              {form.datasheet_url && <FileText className="w-8 h-8 text-signal shrink-0" />}
              <label className="cursor-pointer font-mono text-xs uppercase tracking-wider px-4 py-2.5 border border-white/10 text-slate-300 hover:border-signal hover:text-signal transition-colors flex items-center gap-2">
                {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload PDF
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={uploadDoc} data-testid="pf-datasheet-upload" />
              </label>
              <input className={field + " flex-1"} value={form.datasheet_url} onChange={set("datasheet_url")} data-testid="pf-datasheet-url" placeholder="or paste PDF URL" />
            </div>
            {form.datasheet_url && <a href={mediaUrl(form.datasheet_url)} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-signal mt-1 inline-block">Preview datasheet ↗</a>}
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 text-slate-300 text-sm cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="pf-featured" />
            Featured product
          </label>
        </div>
        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onCancel} className="flex-1 font-head font-bold uppercase tracking-wider px-6 py-3 border border-white/10 text-slate-300 hover:text-white transition-colors">Cancel</button>
          <button disabled={busy} data-testid="pf-save" className="flex-1 font-head font-bold uppercase tracking-wider px-6 py-3 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [partners, setPartners] = useState([]);
  const [syncing, setSyncing] = useState("");

  const loadPartners = async () => { const { data } = await api.get("/partners"); setPartners(data); };
  const syncPartner = async (slug) => {
    setSyncing(slug);
    try {
      const { data } = await api.post(`/partners/${slug}/sync`);
      toast.success(data.ok ? `${slug}: synced ${data.matched}/${data.found} live models` : `${slug}: sync attempted (site unreachable)`);
      await loadPartners();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSyncing(""); }
  };

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/products"), api.get("/stats")]);
    setProducts(p.data); setStats(s.data);
  };
  const loadEnq = async () => { const { data } = await api.get("/enquiries"); setEnquiries(data); };

  useEffect(() => { load(); loadEnq(); loadPartners(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      const s = await api.get("/stats"); setStats(s.data);
      toast.success(`Marked ${status}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const statCards = [
    { icon: Package, label: "Products", value: stats?.total_products },
    { icon: Inbox, label: "Enquiries", value: stats?.total_enquiries },
    { icon: Inbox, label: "New Enquiries", value: stats?.new_enquiries },
    { icon: Layers, label: "Categories", value: stats?.categories },
  ];

  return (
    <div className="min-h-screen grain">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 grid place-items-center bg-signal text-ink font-head font-black text-xl leading-none">D</span>
            <span className="font-head font-extrabold tracking-widest text-white">ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono text-xs text-slate-400 hover:text-signal">View Site</Link>
            <span className="font-mono text-xs text-slate-500 hidden sm:block">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="font-mono text-xs uppercase tracking-wider flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-slate-300 hover:border-signal hover:text-signal transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {statCards.map((s) => (
            <div key={s.label} className="bg-card border border-white/10 p-5" data-testid={`stat-${s.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <s.icon className="w-5 h-5 text-signal mb-3" />
              <div className="font-head font-black text-4xl text-white">{s.value ?? "—"}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          {["products", "enquiries", "partners"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`font-mono text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${tab === t ? "bg-signal text-ink border-signal" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div>
            <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-product-btn"
              className="mb-5 font-head font-bold uppercase tracking-wider px-5 py-2.5 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Product
            </button>
            <div className="bg-card border border-white/10 divide-y divide-white/5" data-testid="admin-product-list">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.slug}`}>
                  <img src={mediaUrl(p.image_url)} alt="" className="w-14 h-14 object-cover border border-white/10 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-head font-bold uppercase text-white truncate">{p.name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{p.brand} · {p.category}</div>
                  </div>
                  <button onClick={() => setEditing({ id: p.id, name: p.name, category: p.category, brand: p.brand, description: p.description, image_url: p.image_url, datasheet_url: p.datasheet_url || "", featured: p.featured, specsText: Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n") })}
                    data-testid={`edit-${p.slug}`} className="w-9 h-9 grid place-items-center border border-white/10 text-slate-400 hover:border-signal hover:text-signal transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-${p.slug}`} className="w-9 h-9 grid place-items-center border border-white/10 text-slate-400 hover:border-red-500 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="bg-card border border-white/10 divide-y divide-white/5" data-testid="admin-enquiry-list">
            {enquiries.length === 0 ? <div className="p-8 font-mono text-sm text-slate-500">No enquiries yet.</div> :
              enquiries.map((e) => {
                const status = e.status || "new";
                const badge = { new: "text-signal border-signal/40 bg-signal/10", contacted: "text-cyan border-cyan/40 bg-cyan/10", closed: "text-slate-400 border-white/10 bg-white/5" }[status];
                return (
                <div key={e.id} className="p-5" data-testid={`enquiry-${e.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="font-head font-bold uppercase text-white flex items-center gap-3">{e.full_name} <span className="text-slate-500 font-body font-normal normal-case">· {e.company || "Individual"}</span>
                      <span className={`font-mono text-[9px] uppercase tracking-widest border px-2 py-0.5 ${badge}`} data-testid={`enquiry-status-${e.id}`}>{status}</span>
                    </span>
                    <span className="font-mono text-[11px] text-slate-500">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  <div className="font-mono text-[11px] text-signal mb-2">{e.email} · {e.phone || "—"} · {e.product_interest || "General"}{e.torque_requirements ? ` · ${e.torque_requirements}` : ""}</div>
                  <p className="text-sm text-slate-400 mb-3">{e.message}</p>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setStatus(e.id, "contacted")} disabled={status === "contacted"} data-testid={`mark-contacted-${e.id}`}
                      className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-white/10 text-slate-300 hover:border-cyan hover:text-cyan transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-slate-300">
                      <Phone className="w-3 h-3" /> Contacted
                    </button>
                    <button onClick={() => setStatus(e.id, "closed")} disabled={status === "closed"} data-testid={`mark-closed-${e.id}`}
                      className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-white/10 text-slate-300 hover:border-signal hover:text-signal transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-slate-300">
                      <Check className="w-3 h-3" /> Closed
                    </button>
                    {status !== "new" && (
                      <button onClick={() => setStatus(e.id, "new")} data-testid={`mark-new-${e.id}`}
                        className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border border-white/10 text-slate-500 hover:text-white transition-colors">
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              );})}
          </div>
        )}

        {tab === "partners" && (
          <div>
            <p className="font-mono text-[11px] text-slate-500 mb-4">Catalogs auto-sync every 12h. Trigger a manual re-scrape to refresh live product images & links now.</p>
            <div className="bg-card border border-white/10 divide-y divide-white/5" data-testid="admin-partner-list">
              {partners.map((p) => (
                <div key={p.slug} className="flex items-center gap-4 p-4" data-testid={`admin-partner-${p.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-head font-bold uppercase text-white">{p.name}</div>
                    <div className="font-mono text-[11px] text-slate-500">
                      {p.product_count} products · {p.last_synced ? `last synced ${new Date(p.last_synced).toLocaleString()}` : "not synced yet"}
                    </div>
                  </div>
                  <button onClick={() => syncPartner(p.slug)} disabled={syncing === p.slug} data-testid={`sync-${p.slug}`}
                    className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 border border-white/10 text-slate-300 hover:border-signal hover:text-signal transition-colors flex items-center gap-2 disabled:opacity-50">
                    <RefreshCw className={`w-3.5 h-3.5 ${syncing === p.slug ? "animate-spin" : ""}`} /> Sync now
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && <ProductForm initial={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
    </div>
  );
}

export default function Admin() {
  const { user, checking, logout } = useAuth();
  if (checking || user === null) return <div className="min-h-screen grid place-items-center bg-ink"><Loader2 className="w-8 h-8 animate-spin text-signal" /></div>;
  if (!user) return <Login />;
  if (user.role !== "admin") return (
    <div className="min-h-screen grid place-items-center tech-grid px-4" data-testid="admin-not-authorized">
      <div className="w-full max-w-sm bg-card border border-white/10 p-8 text-center">
        <div className="font-head font-black uppercase text-3xl text-white mb-2">Access Denied</div>
        <p className="text-slate-400 text-sm mb-6">You're signed in as <span className="text-white">{user.email}</span>, which doesn't have admin access.</p>
        <div className="flex flex-col gap-3">
          <Link to="/" className="font-head font-bold uppercase tracking-wider px-6 py-3 bg-signal text-ink hover:bg-signal-hover transition-colors">Back to site</Link>
          <button onClick={logout} data-testid="denied-logout" className="font-mono text-xs uppercase tracking-wider px-6 py-3 border border-white/15 text-slate-300 hover:text-white transition-colors">Sign out</button>
        </div>
      </div>
    </div>
  );
  return <Dashboard />;
}
